import { useMemo, useState } from "react"
import getColor from "@/utils/get-color.utils"

interface Props {
  //Le store rend les dates telles que l'API les a envoyees : des chaines, une fois rechargees
  endDate: Date | string | null
  onChange: (startDate: Date, endDate: Date) => void
  startDate: Date | string | null
}

function toDate(value: Date | string | null) {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function startOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function endOfMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0)
}

function addMonths(date: Date, amount: number) {
  return new Date(date.getFullYear(), date.getMonth() + amount, 1)
}

function monthKey(date: Date) {
  return date.getFullYear() * 12 + date.getMonth()
}

function toMonthInputValue(date: Date | null) {
  if (!date) return ""
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
}

function fromMonthInputValue(value: string) {
  const [year, month] = value.split("-").map(Number)
  return new Date(year, month - 1, 1)
}

function formatMonth(date: Date) {
  return date.toLocaleDateString("fr-FR", { month: "short" }).replace(".", "")
}

export default function PreventionActionMonthPlanner(props: Props) {
  const { onChange } = props
  const startDate = toDate(props.startDate)
  const endDate = toDate(props.endDate)
  const [rangeStart, setRangeStart] = useState<Date | null>(null)
  const [hoveredMonth, setHoveredMonth] = useState<Date | null>(null)
  const months = useMemo(() => {
    const firstMonth = addMonths(startOfMonth(new Date()), -1)
    return Array.from({ length: 12 }, (_, index) => addMonths(firstMonth, index))
  }, [])

  function selectMonth(month: Date) {
    if (!rangeStart) {
      setRangeStart(month)
      setHoveredMonth(month)
      onChange(startOfMonth(month), endOfMonth(month))
      return
    }

    const first = monthKey(month) < monthKey(rangeStart) ? month : rangeStart
    const last = monthKey(month) < monthKey(rangeStart) ? rangeStart : month
    onChange(startOfMonth(first), endOfMonth(last))
    setRangeStart(null)
    setHoveredMonth(null)
  }

  function isInDisplayedRange(month: Date) {
    const selectedStart = rangeStart ?? startDate
    const selectedEnd = rangeStart ? (hoveredMonth ?? rangeStart) : endDate
    if (!selectedStart || !selectedEnd) return false

    const min = Math.min(monthKey(selectedStart), monthKey(selectedEnd))
    const max = Math.max(monthKey(selectedStart), monthKey(selectedEnd))
    return monthKey(month) >= min && monthKey(month) <= max
  }

  function updateStart(value: string) {
    if (!value) return
    const nextStart = fromMonthInputValue(value)
    const nextEnd = endDate && monthKey(endDate) >= monthKey(nextStart) ? endDate : endOfMonth(nextStart)
    onChange(startOfMonth(nextStart), nextEnd)
    setRangeStart(null)
  }

  function updateEnd(value: string) {
    if (!value) return
    const nextEnd = fromMonthInputValue(value)
    const nextStart = startDate && monthKey(startDate) <= monthKey(nextEnd) ? startDate : startOfMonth(nextEnd)
    onChange(nextStart, endOfMonth(nextEnd))
    setRangeStart(null)
  }

  return (
    <div>
      {/* Pas de titre : la carte du panneau nomme deja l'etape */}
      <div className="grid grid-cols-6 gap-10">
        {months.map((month, index) => {
          const isSelected = isInDisplayedRange(month)
          const isSecondRow = index >= 6
          const isBoundary =
            monthKey(month) === monthKey(rangeStart ?? startDate ?? new Date(0)) ||
            monthKey(month) === monthKey(rangeStart ? (hoveredMonth ?? rangeStart) : (endDate ?? new Date(0)))

          return (
            <button
              key={month.toISOString()}
              type="button"
              className="_hover border-0 bg-transparent p-0"
              onClick={() => selectMonth(month)}
              onMouseEnter={() => rangeStart && setHoveredMonth(month)}>
              {!isSecondRow && (
                <span
                  className={`mb-[6px] block text-sm ${isSelected ? "font-bold" : "grey-t"}`}
                  style={isSelected ? { color: getColor("blue") } : undefined}>
                  {formatMonth(month)}
                </span>
              )}
              <span
                className="block aspect-square w-full rounded-[12px]"
                style={{
                  backgroundColor: isBoundary
                    ? `${getColor("blue")}cc`
                    : isSelected
                      ? `${getColor("blue")}66`
                      : "#f4f6f8"
                }}
              />
              {isSecondRow && (
                <span
                  className={`mt-[6px] block text-sm ${isSelected ? "font-bold" : "grey-t"}`}
                  style={isSelected ? { color: getColor("blue") } : undefined}>
                  {formatMonth(month)}
                </span>
              )}
            </button>
          )
        })}
      </div>

      <div className="mt-20 flex items-end gap-10">
        <MonthInput label="Début" value={toMonthInputValue(startDate)} onChange={updateStart} />
        <div className="grey-t mb-[12px]">→</div>
        <MonthInput label="Fin" value={toMonthInputValue(endDate)} onChange={updateEnd} />
      </div>
    </div>
  )
}

function MonthInput({ label, onChange, value }: { label: string; onChange: (value: string) => void; value: string }) {
  return (
    <label>
      <span className="grey-t mb-[6px] block text-base">{label}</span>
      <input
        type="month"
        className="h-[42px] rounded-[12px] bg-white px-10 text-base font-normal text-slate-900"
        style={{ border: "1px solid #d8d8d8" }}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  )
}
